
import { Component, Vue, Prop, Ref } from 'vue-property-decorator'
import DomUtils from '@/utils/DomUtils'

/** 表示位置の指定 */
export const POSITION_SET = {
  /** 左側に下方向へ */
  LEFT_DOWN: 'LEFT_DOWN',
  /** 下側に左揃えで */
  BOTTOM_ALIGNLEFT: 'BOTTOM_ALIGNLEFT',
}

/** 表示位置の定義型 */
type POSITION_SET_TYPE = (typeof POSITION_SET)[keyof typeof POSITION_SET]

/**
 * クリック時にポップアップするフロート要素
 */
@Component
export default class FloatPanel extends Vue {
  /** パネル部分のHTML要素 */
  @Ref()
  flontPanelElement?: HTMLElement

  /** 表示幅(px) */
  @Prop()
  width!: number

  /** 表示高さ(px) */
  @Prop()
  height!: number

  /** 表示フラグ */
  private isShow = false

  /** 表示位置(縦) */
  private positionTop = 0

  /** 表示位置(横) */
  private positionLeft = 0

  /** 上方向オフセット値(オーバーレイ) */
  private offsetTop = 0

  /** 上方向オフセット値(オーバーレイ) */
  private offsetBottom = 0

  /** 左方向オフセット値(オーバーレイ) */
  private offsetLeft = 0

  /** 右方向オフセット値(オーバーレイ) */
  private offsetRight = 0

  /**
   * 表示位置などcssの設定
   *
   * @returns css変数情報
   */
  private get setStyle(): { [key: string]: string } {
    return {
      '--offset-top': `${this.offsetTop}px`,
      '--offset-bottom': `${this.offsetBottom}px`,
      '--offset-left': `${this.offsetLeft}px`,
      '--offset-right': `${this.offsetRight}px`,
      '--min-height': `${this.minHeight}px`,
      '--width': `${this.width}px`,
      '--height': `${this.height}px`,
      '--top': `${this.positionTop}px`,
      '--left': `${this.positionLeft}px`,
    }
  }

  /**
   * 全体の最低高さ
   *
   * @returns 表示エリアとして確保が必要な高さ(px)
   */
  private get minHeight(): number {
    const minHeight = this.positionTop + this.height
    return Math.max(minHeight, document.body.scrollHeight)
  }

  /**
   * 表示処理
   *
   * @param target 表示位置の基準にするDOM要素
   * @param fitType 位置調整方法
   * @param parentElement 表示エリアの基準となる親DOM要素
   */
  public async show(target: HTMLElement, fitType: POSITION_SET_TYPE, parentElement?: HTMLElement): Promise<void> {
    // 表示基準のDOM要素の絶対座標取得
    const absoluteRect = DomUtils.getAbsoluteRect(target, parentElement)

    // 位置調整
    if (fitType === POSITION_SET.LEFT_DOWN) {
      // 左
      this.positionTop = absoluteRect.y
      this.positionLeft = absoluteRect.x - this.width
    } else if (fitType === POSITION_SET.BOTTOM_ALIGNLEFT) {
      // 中央下
      this.positionTop = absoluteRect.bottom
      this.positionLeft = absoluteRect.x
    } else {
      // デフォルト位置(左上座標基準)
      this.positionTop = absoluteRect.y
      this.positionLeft = absoluteRect.x
    }

    // 表示処理
    this.isShow = true

    // 基準にする親要素の指定がない場合は位置調整を行う
    if (!parentElement) {
      await this.setOffset()
    }
  }

  /**
   * オーバーレイ要素の位置調整
   */
  private async setOffset(): Promise<void> {
    // オーバーレイ要素の表示待ち
    while (!this.flontPanelElement) {
      await this.sleep(10)
    }

    // オーバーレイ要素の表示後に位置調整を行う
    const elementRect = this.flontPanelElement.getBoundingClientRect()
    const documentRect = document.body.getBoundingClientRect()
    // 上位置が0座標と一致しない場合調整
    if (elementRect.top + window.scrollY !== 0) {
      this.offsetTop = 0 - elementRect.top - window.scrollY
    }
    // 左位置が0座標と一致しない場合調整
    if (elementRect.left + window.scrollX !== 0) {
      this.offsetLeft = 0 - elementRect.left - window.scrollX
    }
    // 下位置がドキュメント全体と一致しない場合調整
    if (elementRect.bottom !== documentRect.bottom) {
      this.offsetBottom = 0 - (documentRect.bottom - elementRect.bottom)
    }
    // 右位置がドキュメント全体と一致しない場合調整
    const currentWidth = elementRect.right + window.scrollX
    if (currentWidth !== window.document.body.scrollWidth) {
      this.offsetRight = 0 - (window.document.body.scrollWidth - elementRect.right + window.scrollX)
    }
  }

  /**
   * ウェイト処理
   *
   * @param ms ミリ秒
   */
  private async sleep(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * クローズイベント
   */
  public close(): void {
    this.isShow = false
  }
}
